import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Zap, Star, Repeat, Target, Trophy, HelpCircle, Shuffle } from 'lucide-react';
import './MeaningMatch.css';

const ROUND_SIZE = 5;

function MeaningMatch() {
    // --- Estados del Juego (sin cambios) ---
    const [gameState, setGameState] = useState('menu');
    const [difficulty, setDifficulty] = useState(2);
    const [phrasalVerbs, setPhrasalVerbs] = useState([]);
    const [shuffledMeanings, setShuffledMeanings] = useState([]);
    const [selectedVerb, setSelectedVerb] = useState(null);
    const [selectedMeaning, setSelectedMeaning] = useState(null);
    const [connections, setConnections] = useState([]);
    const [correctMatches, setCorrectMatches] = useState([]);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('meaningMatchHighScore') || '0', 10));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const svgRef = useRef(null);

    // --- Lógica del Juego (sin cambios funcionales) ---
    const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

    const startGame = async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-meaning-match-round', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    difficulty, 
                    count: ROUND_SIZE, 
                    seen_verbs: [] 
                }),
            });
            if (!response.ok) throw new Error('No se encontraron Phrasal Verbs para esta dificultad.');
            
            const data = await response.json();
            if (data.length < ROUND_SIZE) throw new Error('No hay suficientes Phrasal Verbs en la base de datos.');

            setPhrasalVerbs(data);
            setShuffledMeanings(shuffleArray(data));
            setScore(0);
            setStreak(0);
            setConnections([]);
            setCorrectMatches([]);
            setSelectedVerb(null);
            setSelectedMeaning(null);
            setGameState('playing');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedVerb && selectedMeaning) {
            const isCorrect = selectedVerb.phrasal_verb === selectedMeaning.phrasal_verb;
            setConnections(prev => [...prev, { from: selectedVerb.phrasal_verb, to: selectedMeaning.meaning_es, isCorrect }]);
            if (isCorrect) {
                const points = 10 + streak * 2;
                setScore(s => s + points);
                setStreak(s => s + 1);
                setCorrectMatches(prev => [...prev, selectedVerb.phrasal_verb]);
            } else {
                setStreak(0);
                setTimeout(() => {
                    setConnections(prev => prev.filter(c => c.isCorrect));
                }, 800);
            }
            setSelectedVerb(null);
            setSelectedMeaning(null);
        }
    }, [selectedVerb, selectedMeaning, streak]);

    useEffect(() => {
        if (phrasalVerbs.length > 0 && correctMatches.length === phrasalVerbs.length) {
            if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('meaningMatchHighScore', score.toString());
            }

            // Guardar XP en el backend
            const saveScore = async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) return;
                    
                    if (correctMatches.length > 0) {
                        await fetch('/api/minigames/save-score', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                game_name: 'meaning_match',
                                score: score,
                                correct_answers: correctMatches.length,
                                total_questions: phrasalVerbs.length
                            })
                        });
                    }
                } catch (error) {
                    console.error("Error guardando XP:", error);
                }
            };
            saveScore();

            setTimeout(() => {
                setGameState('gameOver');
            }, 1000);
        }
    }, [correctMatches, phrasalVerbs, score, highScore]);
    
    const resetGame = () => {
        setGameState('menu');
        setError('');
    };

    // --- Renderizado de Componentes con Clases "mm-" ---

    const SvgConnections = () => {
        const [lines, setLines] = useState([]);
        useEffect(() => {
            const svgEl = svgRef.current;
            if (!svgEl) return;
            const svgRect = svgEl.getBoundingClientRect();
            const newLines = connections.map((conn, index) => {
                const fromEl = document.getElementById(`phrasal-${conn.from}`);
                const toEl = document.getElementById(`meaning-${conn.to}`);
                if (!fromEl || !toEl) return null;
                const fromRect = fromEl.getBoundingClientRect();
                const toRect = toEl.getBoundingClientRect();
                return {
                    key: `${conn.from}-${conn.to}-${index}`,
                    x1: fromRect.right - svgRect.left,
                    y1: fromRect.top + fromRect.height / 2 - svgRect.top,
                    x2: toRect.left - svgRect.left,
                    y2: toRect.top + toRect.height / 2 - svgRect.top,
                    isCorrect: conn.isCorrect,
                };
            }).filter(Boolean);
            setLines(newLines);
        }, [connections]);
        return (
            <svg ref={svgRef} className="mm-connection-svg">
                {lines.map(line => (
                    <line key={line.key} {...line} className={line.isCorrect ? 'correct' : 'incorrect'} />
                ))}
            </svg>
        );
    };

    if (gameState === 'menu') {
        return (
            <div className="mm-container">
                <div className="mm-inner-container">
                    <div className="mm-menu">
                        <div className="mm-header">
                            <Shuffle size={40}/>
                            <h1>Meaning Match</h1>
                            <p>Conecta el Phrasal Verb con su significado correcto.</p>
                        </div>
                        <div className="mm-dashboard">
                            <div><Star size={16} /><span>Puntaje Máx.</span><strong>{highScore}</strong></div>
                            <div><Target size={16} /><span>Meta</span><strong>{ROUND_SIZE} </strong></div>
                            <div><Zap size={16} /><span>Racha</span><strong>0</strong></div>
                        </div>
                        <div className="mm-config-section">
                            <h3>Elige la Dificultad</h3>
                            <div className="mm-difficulty-options">
                                <button onClick={() => setDifficulty(1)} className={`easy ${difficulty === 1 ? 'active' : ''}`}>Fácil</button>
                                <button onClick={() => setDifficulty(2)} className={`medium ${difficulty === 2 ? 'active' : ''}`}>Intermedio</button>
                                <button onClick={() => setDifficulty(3)} className={`hard ${difficulty === 3 ? 'active' : ''}`}>Difícil</button>
                            </div>
                        </div>
                        {error && <p className="mm-error-message">{error}</p>}
                        <button className="mm-play-btn" onClick={startGame} disabled={isLoading}>
                            {isLoading ? 'Cargando...' : '¡Comenzar!'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (gameState === 'gameOver') {
        return (
            <div className="mm-container">
                <div className="mm-inner-container">
                    <div className="mm-game-over">
                        <Trophy size={48}/>
                        <h2>¡Ronda Completada!</h2>
                        <p>Tu puntaje final fue:</p>
                        <strong className="mm-final-score">{score}</strong>
                        <button className="mm-play-btn" onClick={resetGame}><Repeat size={16}/> Jugar de Nuevo</button>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="mm-container">
            <div className="mm-inner-container playing">
                <div className="mm-game-hud">
                    <div className="mm-hud-item score"><Star size={16}/> {score}</div>
                    <div className="mm-hud-item progress"><Target size={16}/> {correctMatches.length} / {ROUND_SIZE}</div>
                    <div className="mm-hud-item streak"><Zap size={16}/> x{streak}</div>
                </div>

                <div className="mm-match-area">
                    <SvgConnections />
                    <div className="mm-column">
                        {phrasalVerbs.map(verb => (
                            <button
                                key={verb.phrasal_verb}
                                id={`phrasal-${verb.phrasal_verb}`}
                                className={`mm-match-item phrasal ${selectedVerb?.phrasal_verb === verb.phrasal_verb ? 'selected' : ''} ${correctMatches.includes(verb.phrasal_verb) ? 'matched' : ''}`}
                                onClick={() => setSelectedVerb(verb)}
                                disabled={correctMatches.includes(verb.phrasal_verb)}
                            >
                                <span>{verb.phrasal_verb}</span>
                                <span className="mm-emoji-icon">{verb.emoji}</span>
                            </button>
                        ))}
                    </div>
                    <div className="mm-column">
                        {shuffledMeanings.map(verb => (
                            <button
                                key={verb.meaning_es}
                                id={`meaning-${verb.meaning_es}`}
                                className={`mm-match-item meaning ${selectedMeaning?.meaning_es === verb.meaning_es ? 'selected' : ''} ${correctMatches.includes(verb.phrasal_verb) ? 'matched' : ''}`}
                                onClick={() => setSelectedMeaning(verb)}
                                disabled={correctMatches.includes(verb.phrasal_verb)}
                            >
                                {verb.meaning_es}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MeaningMatch;